import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '../clients/APIClient';
import { ModelPackInfo } from '../types/models';

interface ModelImportModalProps {
  onClose: () => void;
  // Called after a successful import so the caller can refresh its model list.
  onImported?: (models: string[]) => void;
}

function formatSize(bytes: number): string {
  if (!bytes) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Admin tool to import model packs that an operator has staged on the appliance
 * (SSD/USB -> the import directory). Lists detected packs and imports them into
 * the model store; the backend refreshes the registry live (no restart).
 */
export function ModelImportModal({ onClose, onImported }: ModelImportModalProps) {
  const [packs, setPacks] = useState<ModelPackInfo[]>([]);
  const [importDir, setImportDir] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyPack, setBusyPack] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.models.getAvailableModelPacks();
      setPacks(res.data);
      setImportDir(res.import_dir);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to scan for model packs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleImport = useCallback(
    async (pack: string) => {
      setBusyPack(pack);
      setError(null);
      setStatus(null);
      try {
        const res = await apiClient.models.importModelPack(pack);
        if (res.imported) {
          setStatus(`Imported ${pack}: ${res.models.join(', ') || 'no new models'}`);
          onImported?.(res.models);
          await refresh();
        } else {
          setError(res.message || `Failed to import ${pack}.`);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : `Failed to import ${pack}.`);
      } finally {
        setBusyPack(null);
      }
    },
    [onImported, refresh]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      setError(null);
      setStatus(null);
      setUploadPct(0);
      try {
        const res = await apiClient.models.uploadModelPack(file, setUploadPct);
        if (res.imported) {
          setStatus(`Imported ${file.name}: ${res.models.join(', ') || 'no new models'}`);
          onImported?.(res.models);
          await refresh();
        } else {
          setError(res.message || `Failed to import ${file.name}.`);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : `Upload failed.`);
      } finally {
        setUploadPct(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [onImported, refresh]
  );

  return (
    <div
      className="fixed inset-0 z-[1001] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[85vh] rounded-2xl border border-gray-800 bg-zinc-950 text-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold">Import Models</h2>
            <p className="text-xs text-red-100/90">
              Packs staged in <code className="font-mono">{importDir || 'the import folder'}</code>
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md px-2 py-1 text-red-100 hover:bg-red-800/60"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto flex-1">
          <div className="flex items-center justify-between mb-3 gap-3">
            <p className="text-sm text-gray-400">
              Upload an <code>.er</code> model file, or import one staged on the
              appliance. No restart needed.
            </p>
            <div className="flex items-center gap-2 flex-shrink-0">
              <input
                ref={fileInputRef}
                type="file"
                accept=".er,.tar,.tgz,.gz"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleUpload(f);
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadPct !== null}
                className="text-xs rounded-md bg-red-600 px-3 py-1 font-medium hover:bg-red-500 disabled:opacity-50"
              >
                {uploadPct !== null ? `Uploading… ${uploadPct}%` : 'Upload .er'}
              </button>
              <button
                onClick={refresh}
                disabled={loading || uploadPct !== null}
                className="text-xs rounded-md border border-gray-700 px-2 py-1 hover:bg-zinc-800 disabled:opacity-50"
              >
                {loading ? 'Scanning…' : 'Rescan'}
              </button>
            </div>
          </div>

          {uploadPct !== null && (
            <div className="mb-3 h-2 w-full overflow-hidden rounded bg-zinc-800">
              <div
                className="h-full bg-red-600 transition-[width] duration-150"
                style={{ width: `${uploadPct}%` }}
              />
            </div>
          )}

          {error && (
            <div className="mb-3 rounded-md border border-red-800 bg-red-950/60 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}
          {status && (
            <div className="mb-3 rounded-md border border-green-800 bg-green-950/60 px-3 py-2 text-sm text-green-200">
              {status}
            </div>
          )}

          {!loading && packs.length === 0 && (
            <div className="rounded-md border border-gray-800 bg-zinc-900 px-4 py-6 text-center text-sm text-gray-400">
              No model packs found. Copy a pack into
              <code className="mx-1 font-mono text-gray-300">{importDir || '…/models/import'}</code>
              and press Rescan.
            </div>
          )}

          <ul className="space-y-2">
            {packs.map((pack) => (
              <li
                key={pack.pack}
                className="flex items-center justify-between rounded-lg border border-gray-800 bg-zinc-900 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{pack.pack}</span>
                    {pack.gpu_ready && (
                      <span className="rounded bg-emerald-900/70 px-1.5 py-0.5 text-[10px] text-emerald-200">
                        GPU
                      </span>
                    )}
                    {pack.already_installed && (
                      <span className="rounded bg-amber-900/70 px-1.5 py-0.5 text-[10px] text-amber-200">
                        installed
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {pack.models.join(', ') || 'no models declared'} · {formatSize(pack.size_bytes)}
                  </div>
                </div>
                <button
                  onClick={() => handleImport(pack.pack)}
                  disabled={busyPack !== null}
                  className="ml-3 flex-shrink-0 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium hover:bg-red-500 disabled:opacity-50"
                >
                  {busyPack === pack.pack ? 'Importing…' : pack.already_installed ? 'Reimport' : 'Import'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default ModelImportModal;
